/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import type { ReactElement } from 'react';
import React from 'react';
import { List } from 'react-window';
import type { RowComponentProps } from 'react-window';
import type { ServiceInterface } from '@opengeoweb/shared';
import type { layerTypes, serviceTypes } from '@opengeoweb/store';
import type { DeleteLayerParams } from './LayerAddRemoveButton';
import { LayerListRow } from './LayerListRow';
import type { LayerWithServiceName } from '../../store/types';
import { useLayerSelectTranslation } from '../../utils/i18n';
import { widthToRowHeight } from '../LayerSelect/LayerSelectUtils';

interface AddLayerParams {
  serviceUrl: string;
  layerName: string;
  serviceType?: ServiceInterface;
}

export interface LayerListProps {
  services: serviceTypes.Services;
  filteredLayers: LayerWithServiceName[];
  layerSelectHeight: number;
  serviceListHeight: number;
  layerSelectWidth: number;
  allLayers: layerTypes.ReduxLayer[];
  allMapIds: string[];
  addLayer: ({ serviceUrl, layerName }: AddLayerParams) => void;
  addLayerToAllMaps: ({ serviceUrl, layerName }: AddLayerParams) => void;
  deleteLayer: ({ layerId }: DeleteLayerParams) => void;
  removeLayerFromAllMaps: (layerId: string) => void;
  mapLayers: layerTypes.ReduxLayer[];
  searchFilter: string;
  isLayerInfoVisibleInStore: boolean;
  onToggleActiveLayer: (
    params: AddLayerParams & { isActiveLayer: boolean },
  ) => void;
  currentLayerInfo?: AddLayerParams;
}

type LayerRowExtraProps = Pick<
  LayerListProps,
  | 'services'
  | 'filteredLayers'
  | 'allLayers'
  | 'allMapIds'
  | 'addLayer'
  | 'addLayerToAllMaps'
  | 'deleteLayer'
  | 'removeLayerFromAllMaps'
  | 'mapLayers'
  | 'searchFilter'
  | 'isLayerInfoVisibleInStore'
  | 'onToggleActiveLayer'
  | 'currentLayerInfo'
  | 'layerSelectWidth'
> & {
  rowMargin: number;
};

const LayerRow = ({
  index,
  style,
  services,
  filteredLayers,
  allLayers,
  allMapIds,
  addLayer,
  addLayerToAllMaps,
  deleteLayer,
  removeLayerFromAllMaps,
  mapLayers,
  searchFilter,
  isLayerInfoVisibleInStore,
  onToggleActiveLayer,
  currentLayerInfo,
  layerSelectWidth,
  rowMargin,
}: RowComponentProps<LayerRowExtraProps>): ReactElement | null => {
  const layer = filteredLayers[index];
  const service = services[layer.serviceName];

  if (!service) {
    return <div style={style} />;
  }

  const isActiveLayer =
    isLayerInfoVisibleInStore &&
    currentLayerInfo?.serviceUrl === service.serviceUrl &&
    currentLayerInfo?.layerName === layer.name;

  const rowStyle: React.CSSProperties = {
    ...style,
    boxSizing: 'border-box',
    paddingBottom: rowMargin,
  };

  return (
    <div key={layer.name} style={rowStyle}>
      <LayerListRow
        addLayer={addLayer}
        addLayerToAllMaps={addLayerToAllMaps}
        allLayers={allLayers}
        allMapIds={allMapIds}
        deleteLayer={deleteLayer}
        hasActiveLayer={isActiveLayer}
        layer={layer}
        layerSelectWidth={layerSelectWidth}
        mapLayers={mapLayers}
        onToggleActiveLayer={() =>
          onToggleActiveLayer({
            serviceUrl: service.serviceUrl!,
            layerName: layer.name!,
            isActiveLayer,
          })
        }
        removeLayerFromAllMaps={removeLayerFromAllMaps}
        searchFilter={searchFilter}
        service={service}
      />
    </div>
  );
};

export const LayerList: React.FC<LayerListProps> = ({
  services,
  filteredLayers,
  layerSelectHeight,
  serviceListHeight,
  layerSelectWidth,
  allLayers,
  allMapIds,
  addLayer,
  addLayerToAllMaps,
  deleteLayer,
  removeLayerFromAllMaps,
  mapLayers,
  searchFilter,
  isLayerInfoVisibleInStore,
  onToggleActiveLayer,
  currentLayerInfo,
}: LayerListProps) => {
  const { t } = useLayerSelectTranslation();

  const rowMargin = 4;
  const rowHeight = widthToRowHeight(layerSelectWidth);

  const layerSelectFilterHeight = 155 + serviceListHeight;
  const minHeight = 320;

  const listHeight =
    layerSelectHeight - layerSelectFilterHeight < minHeight
      ? minHeight
      : layerSelectHeight - layerSelectFilterHeight;

  return (
    <Box data-testid="layerList">
      <Box sx={{ marginBottom: '4px', fontSize: '12px' }}>
        {filteredLayers.length} {t('layer-list-results')}
      </Box>

      <List
        defaultHeight={minHeight}
        rowComponent={LayerRow}
        rowCount={filteredLayers.length}
        rowHeight={rowHeight + rowMargin}
        rowProps={{
          services,
          filteredLayers,
          allLayers,
          allMapIds,
          addLayer,
          addLayerToAllMaps,
          deleteLayer,
          removeLayerFromAllMaps,
          mapLayers,
          searchFilter,
          isLayerInfoVisibleInStore,
          onToggleActiveLayer,
          currentLayerInfo,
          layerSelectWidth,
          rowMargin,
        }}
        style={{ height: listHeight, width: '100%' }}
      />
    </Box>
  );
};
